/** Optional LLM via assistant-server proxy (Ollama local or Gemini cloud). */

import { getUserRoleBucket } from "assistant/userRole";
import { isGeminiOffered } from "utils/assistantAiMode";

export function isLlmEnabled() {
  return (
    process.env.REACT_APP_ASSISTANT_LLM_ENABLED === "true" &&
    Boolean(process.env.REACT_APP_ASSISTANT_LLM_URL)
  );
}

/** Which backend the user selected (when Smart AI is on). */
export function resolveLlmProvider(context) {
  if (context?.useGemini && isGeminiOffered()) return "gemini";
  return "ollama";
}

export { isGeminiOffered };

function llmBaseUrl() {
  return String(process.env.REACT_APP_ASSISTANT_LLM_URL || "").replace(/\/+$/, "");
}

/** Keep LLM payloads tiny — never send photos / full row lists. */
function slimRuleFacts(ruleFacts) {
  if (!ruleFacts || typeof ruleFacts !== "object") return null;
  const structuredText = String(ruleFacts.structuredText || "").slice(0, 4000);
  const pageAwareSummary =
    typeof ruleFacts.pageAware?.summary === "string"
      ? ruleFacts.pageAware.summary.slice(0, 800)
      : typeof ruleFacts.pageAware === "string"
        ? ruleFacts.pageAware.slice(0, 800)
        : null;

  return {
    intent: ruleFacts.intent || "general",
    structuredText,
    pageHelp: ruleFacts.pageHelp
      ? {
          title: ruleFacts.pageHelp.title,
          summary: String(ruleFacts.pageHelp.summary || "").slice(0, 500),
          steps: Array.isArray(ruleFacts.pageHelp.steps)
            ? ruleFacts.pageHelp.steps.slice(0, 8).map((s) => String(s).slice(0, 200))
            : [],
        }
      : null,
    faq: ruleFacts.faq
      ? {
          question: String(ruleFacts.faq.question || "").slice(0, 300),
          answer: String(ruleFacts.faq.answer || "").slice(0, 800),
        }
      : null,
    pageAware: pageAwareSummary ? { summary: pageAwareSummary } : null,
    // Omit heavy facts blob entirely (teachers lists, profile pics, etc.)
    facts: null,
  };
}

function shortenErrorDetail(text) {
  const raw = String(text ?? "");
  if (/PayloadTooLargeError|request entity too large/i.test(raw)) {
    return "Payload too large for assistant-server — restart assistant-server after update (limit raised).";
  }
  if (/<!DOCTYPE html>|<html/i.test(raw)) {
    const pre = raw.match(/<pre>([\s\S]*?)<\/pre>/i);
    const inner = (pre?.[1] || raw)
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return inner.slice(0, 280) || "assistant-server returned an HTML error";
  }
  return raw.slice(0, 400);
}

import { extractJsonObject } from "assistant/llmClientUtils";

function buildLlmRequestBody(message, context, options = {}) {
  const { mode = "chat", ruleFacts = null } = options;
  return {
    message: String(message ?? "").slice(0, 2000),
    language: context.language === "en" ? "en" : "fr",
    pathname: context.pathname || "/",
    role: getUserRoleBucket(context.user),
    mode,
    llmProvider: resolveLlmProvider(context),
    ruleFacts: slimRuleFacts(ruleFacts),
    history: Array.isArray(context.conversationHistory) ? context.conversationHistory.slice(-6) : [],
  };
}

function buildLlmHeaders(context) {
  const token = localStorage.getItem("tk446655440000");
  if (!token) return null;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "X-Llm-Provider": resolveLlmProvider(context),
  };
  const apiKey = (process.env.REACT_APP_ASSISTANT_LLM_KEY || "").trim();
  if (apiKey) headers["X-Assistant-Key"] = apiKey;
  return headers;
}

export async function askLocalLlm(message, context, options = {}) {
  if (!isLlmEnabled()) {
    return { ok: false, error: "disabled", detail: "LLM not enabled in .env — restart npm start after editing .env" };
  }

  const headers = buildLlmHeaders(context);
  if (!headers) {
    return { ok: false, error: "auth", detail: "Not logged in" };
  }

  const { mode = "chat" } = options;
  const useGemini = resolveLlmProvider(context) === "gemini";
  const timeoutMs =
    mode === "classify" || mode === "route"
      ? useGemini
        ? 30_000
        : 45_000
      : useGemini
        ? 60_000
        : 95_000;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${llmBaseUrl()}/chat`, {
      method: "POST",
      headers,
      signal: controller.signal,
      body: JSON.stringify(buildLlmRequestBody(message, context, options)),
    });

    const bodyText = await res.text();
    let data = {};
    try {
      data = bodyText ? JSON.parse(bodyText) : {};
    } catch {
      data = { error: bodyText };
    }

    if (!res.ok) {
      const detail = shortenErrorDetail(data?.error || bodyText || res.statusText);
      console.warn("[Assistant LLM]", res.status, detail);
      return { ok: false, error: "http", status: res.status, detail };
    }

    const reply = data?.reply?.trim();
    if (!reply) {
      return { ok: false, error: "empty", detail: "Model returned empty response" };
    }

    return { ok: true, reply };
  } catch (err) {
    const detail = err?.message || String(err);
    console.warn("[Assistant LLM] unavailable", detail);
    return {
      ok: false,
      error: "network",
      detail:
        detail.includes("abort") || detail.includes("Abort")
          ? "Request timed out — model may still be loading"
          : detail,
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Stream tokens from assistant-server (SSE). onUpdate receives cumulative text. */
export async function streamLocalLlm(message, context, options = {}, onUpdate) {
  if (!isLlmEnabled()) {
    return { ok: false, error: "disabled", detail: "LLM not enabled" };
  }

  const headers = buildLlmHeaders(context);
  if (!headers) {
    return { ok: false, error: "auth", detail: "Not logged in" };
  }

  const { mode = "chat" } = options;
  const useGemini = resolveLlmProvider(context) === "gemini";
  const timeoutMs =
    mode === "classify" || mode === "route"
      ? useGemini
        ? 30_000
        : 45_000
      : useGemini
        ? 60_000
        : 95_000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${llmBaseUrl()}/chat/stream`, {
      method: "POST",
      headers,
      signal: controller.signal,
      body: JSON.stringify(buildLlmRequestBody(message, context, options)),
    });

    if (!res.ok) {
      const bodyText = await res.text();
      let data = {};
      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch {
        data = { error: bodyText };
      }
      const detail = shortenErrorDetail(data?.error || bodyText || res.statusText);
      return { ok: false, error: "http", status: res.status, detail };
    }

    const reader = res.body?.getReader?.();
    if (!reader) {
      return { ok: false, error: "network", detail: "Streaming not supported" };
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let reply = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        const line = part
          .split("\n")
          .find((l) => l.startsWith("data:"));
        if (!line) continue;

        let payload;
        try {
          payload = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }

        if (payload?.error) {
          return { ok: false, error: "stream", detail: payload.error };
        }

        if (typeof payload?.chunk === "string") {
          reply = payload.chunk;
          onUpdate?.(reply);
        }

        if (payload?.done) {
          reply = String(payload.reply || reply).trim();
          onUpdate?.(reply);
          return { ok: true, reply };
        }
      }
    }

    if (reply.trim()) {
      return { ok: true, reply: reply.trim() };
    }
    return { ok: false, error: "empty", detail: "Model returned empty response" };
  } catch (err) {
    const detail = err?.message || String(err);
    return {
      ok: false,
      error: "network",
      detail:
        detail.includes("abort") || detail.includes("Abort")
          ? "Request timed out — model may still be loading"
          : detail,
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Classify a user message into a structured write-command draft (JSON). */
export async function classifyWriteIntent(message, context) {
  const result = await askLocalLlm(message, context, {
    mode: "classify",
    ruleFacts: { intent: "classify_write" },
  });
  if (!result?.ok) return { ok: false, ...result };
  const parsed = extractJsonObject(result.reply);
  if (!parsed || typeof parsed !== "object") {
    return { ok: false, error: "parse", detail: "Classifier returned non-JSON" };
  }
  return { ok: true, classified: parsed };
}

export async function checkLlmHealth(context) {
  if (!isLlmEnabled()) return { available: false, reason: "disabled" };
  try {
    const url = `${llmBaseUrl()}/health`;
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return { available: false, reason: `health ${res.status}` };
    const data = await res.json();
    const useGemini = context?.useGemini && isGeminiOffered();
    const backendOk = useGemini ? data?.gemini === "ok" : data?.ollama === "ok";
    return {
      available: backendOk,
      geminiAvailable: data?.gemini === "ok",
      ollamaAvailable: data?.ollama === "ok",
      geminiConfigured: Boolean(data?.geminiConfigured),
      ...data,
    };
  } catch (err) {
    return { available: false, reason: err?.message || "unreachable" };
  }
}
